import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { LoaderCircle } from "lucide-react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { PersonSearch, type ChosenPerson } from "@/components/medico/person-search";
import { Button } from "@/components/ui/button";
import { FinanceiroLabels } from "@/lib/labels/financeiro";

function personId(person: ChosenPerson) {
	if (person.kind === "patient") {
		return person.patientId;
	}

	return person.leadId;
}

function ImportReviewRow({
	item,
}: {
	item: {
		id: number;
		target_kind: string | null;
		person_name: string | null;
		phone: string | null;
		occurred_on: string | null;
		amount_cents: number | null;
		description: string | null;
	};
}) {
	const [person, setPerson] = useState<ChosenPerson | null>(null);
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.people.resolveImport.mutationOptions({
			onError: () => toast.error("Não foi possível vincular a pessoa"),
			onSuccess: async () => {
				await Promise.all([
					queryClient.invalidateQueries({ queryKey: orpc.people.key() }),
					queryClient.invalidateQueries({ queryKey: orpc.agenda.key() }),
					queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() }),
				]);
			},
		}),
	);

	return (
		<div className="flex flex-col gap-3 p-4">
			<div>
				<p className="text-sm font-medium text-foreground">
					{item.target_kind === "receipt" && "Recebimento sem pessoa"}
					{item.target_kind === "appointment" && "Horário sem pessoa"}
				</p>
				<div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
					<span>{item.person_name ?? "Nome ausente"}</span>
					{item.phone !== null && <span>{item.phone}</span>}
					{item.occurred_on !== null && <span>{item.occurred_on}</span>}
					{item.amount_cents !== null && <span>{FinanceiroLabels.money(item.amount_cents)}</span>}
				</div>
				{item.description !== null && (
					<p className="text-xs text-muted-foreground">{item.description}</p>
				)}
			</div>
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
				<div className="min-w-0 flex-1">
					<PersonSearch
						chosen={person}
						onChoose={setPerson}
						initialSearch={item.person_name ?? ""}
						disabled={isPending}
					/>
				</div>
				<Button
					type="button"
					disabled={person === null || isPending}
					onClick={() => {
						if (person !== null) {
							mutate({ id: item.id, person_id: personId(person) });
						}
					}}
				>
					{isPending && <LoaderCircle aria-hidden="true" className="animate-spin" />}
					{isPending && "Vinculando pessoa..."}
					{!isPending && "Vincular pessoa"}
				</Button>
			</div>
		</div>
	);
}

export function PessoasImportReview() {
	const { data, isLoading, isError, isFetching, refetch } = useQuery(
		orpc.people.review.queryOptions(),
	);

	if (isLoading) {
		return (
			<p role="status" className="text-sm text-muted-foreground">
				Carregando importações...
			</p>
		);
	}

	if (isError) {
		return (
			<div
				role="alert"
				className="flex flex-wrap items-center justify-between gap-3 surface-panel p-4"
			>
				<p className="text-sm text-muted-foreground">Não foi possível carregar as importações.</p>
				<Button type="button" variant="outline" onClick={() => refetch()} disabled={isFetching}>
					Tentar novamente
				</Button>
			</div>
		);
	}

	if (data === undefined || data.imports.length === 0) {
		return null;
	}

	return (
		<section className="surface-panel overflow-hidden border-warning/30">
			<div className="border-b border-border px-4 py-3">
				<h2 className="type-section-title">Importações para revisar</h2>
			</div>
			<div className="divide-y divide-border">
				{data.imports.map((item) => (
					<ImportReviewRow key={item.id} item={item} />
				))}
			</div>
		</section>
	);
}
